import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BTN, BTN_PRIMARY, EventChips, StatusBadge } from "@/components/postbacks/shared";
import { FireList } from "@/components/postbacks/FireLog";

const KIND_LABEL = { pixel: "Browser pixel (retired)", connection: "Ad platform connection", postback: "Postback" };
const PLATFORM_LABEL = { meta: "Meta", tiktok: "TikTok", ga4: "Google Analytics 4" };

function ConnectionDetails({ item }) {
  const names = item.event_names || {};
  return (
    <div className="space-y-2 text-sm" data-testid="review-connection">
      <div><span className="label-mono">Platform</span> {PLATFORM_LABEL[item.platform] || item.platform}</div>
      <div><span className="label-mono">ID</span> <span className="font-mono">{item.pixel_id}</span></div>
      <div><span className="label-mono">Token</span> <span className="font-mono">{item.secret_hint || "saved"}</span> (stored encrypted)</div>
      <EventChips events={item.events} testId="review-events" />
      {Object.keys(names).length > 0 && (
        <div className="text-[11px] font-mono">{Object.entries(names).map(([k, v]) => `${k} → ${v}`).join(" · ")}</div>
      )}
      <div className="text-[11px] text-zinc-600">Sent from our server only. Shares the visitor&apos;s IP, browser and the platform&apos;s own click ID, never names, phones or emails.</div>
    </div>
  );
}

function Details({ item, scan }) {
  if (item.kind === "connection") return <ConnectionDetails item={item} />;
  if (item.kind === "pixel") {
    return (
      <div className="space-y-2">
        <div className="label-mono">Runs on: {item.where === "thank_you" ? "Thank-you page" : "Landing pages"} · only for this affiliate&apos;s visitors</div>
        {scan && (
          <div data-testid="review-scan" data-verdict={scan.verdict}
            className={`text-xs border px-2 py-1 ${scan.verdict === "blocked" ? "border-red-700 bg-red-50 text-red-800" : scan.verdict === "pass" ? "border-emerald-700 bg-emerald-50" : "border-amber-600 bg-amber-50"}`}>
            Safety check: <strong>{scan.verdict}</strong>
            {[...(scan.hard_blocks || []), ...(scan.warnings || [])].length > 0 && ` · ${[...(scan.hard_blocks || []), ...(scan.warnings || [])].join(", ")}`}
          </div>
        )}
        <pre className="font-mono text-[11px] bg-zinc-50 border border-zinc-300 p-2 max-h-56 overflow-auto whitespace-pre-wrap break-all" data-testid="review-code">{item.code_snippet}</pre>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="font-mono text-[11px] break-all bg-zinc-50 border border-zinc-300 p-2" data-testid="review-url">{item.url}</div>
      <EventChips events={item.events} testId="review-events" />
    </div>
  );
}

function ReviewActions({ item, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (path, body, msg) => {
    setBusy(true);
    try {
      await api.post(`/admin/postbacks/${item.id}/${path}`, body);
      toast.success(msg);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "That didn't work");
    } finally {
      setBusy(false);
    }
  };
  const canApprove = ["pending", "rejected", "disabled"].includes(item.status);
  const canReject = ["pending", "approved"].includes(item.status);
  return (
    <div className="border-t border-black pt-3 space-y-2">
      {canReject && (
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Reason (needed to reject, shown to them)"
          className="border border-black px-3 py-2 w-full text-sm" data-testid="review-note-input" />
      )}
      <div className="flex flex-wrap gap-2 justify-end">
        {item.status === "approved" && (
          <button type="button" disabled={busy} onClick={() => act("disable", {}, "Turned off")} className={BTN} data-testid="review-disable">Turn off</button>
        )}
        {canReject && (
          <button type="button" disabled={busy || !note.trim()} onClick={() => act("reject", { note: note.trim() }, "Rejected")}
            className={`${BTN} text-red-700 border-red-700`} data-testid="review-reject">Reject</button>
        )}
        {canApprove && item.kind !== "pixel" && (
          <button type="button" disabled={busy} onClick={() => act("approve", {}, "Approved. It's live now")} className={BTN_PRIMARY} data-testid="review-approve">
            Approve
          </button>
        )}
      </div>
    </div>
  );
}

export function AdminReviewDialog({ id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/postbacks/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Couldn't load it"));
  }, [id]);
  const done = () => {
    onChanged();
    onClose();
  };
  const item = data?.item;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none border-2 border-black sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="postback-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo text-xl font-black">{item ? item.name : "Loading…"}</DialogTitle>
          {item && (
            <DialogDescription>
              {KIND_LABEL[item.kind] || "Postback"} from {item.owner_name} ({item.owner_type === "affiliate" ? "affiliate" : "media buyer"})
              · added by {item.created_by_name}
            </DialogDescription>
          )}
        </DialogHeader>
        {item && (
          <div className="space-y-4">
            <StatusBadge row={item} testId="review-status" />
            <Details item={item} scan={data.scan} />
            {item.kind !== "pixel" && (
              <div>
                <div className="label-mono mb-1">Recently sent</div>
                <FireList fires={data.fires} testId="review-fires" />
              </div>
            )}
            <ReviewActions item={item} onDone={done} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
